import { appAlert } from '@/lib/appAlert';
import { getErrorMessage } from '@/lib/functions';
import { Ionicons } from '@expo/vector-icons';
﻿import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Button } from '@/components/ui/Button';
import { ConfirmModal } from '@/components/ConfirmModal';
import { StarRating } from '@/components/ui/StarRating';
import { WizardLayout } from '@/components/ui/WizardLayout';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import type { ReviewTag } from '@/lib/database.types';
import { createReview, NEGATIVE_TAGS, pickReviewPhoto, POSITIVE_TAGS, tagLabel, tagSentiment } from '@/lib/reviews';
import { getConnectionStatus, sendConnectionRequest } from '@/lib/connections';
import { ErrorText } from '@/components/ErrorText';

export default function ReviewScreen() {
  const { agreementId, revieweeId, revieweeName, queueIds, queueNames, allowPhoto } = useLocalSearchParams<{
    agreementId: string;
    revieweeId: string;
    revieweeName?: string;
    /** Συμφωνίες πολλών ατόμων: υπόλοιποι προς αξιολόγηση, ένας τη φορά — "|"-χωρισμένα ids/ονόματα. */
    queueIds?: string;
    queueNames?: string;
    /** Μόνο για συμφωνίες που προήλθαν από προσφορά επαγγελματία (agreement.from_offer). */
    allowPhoto?: string;
  }>();
  const { user } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { t } = useTranslation();

  // Ουρά αξιολογήσεων: για 2-ατόμων είναι απλά [revieweeId]· για πολλών ατόμων
  // ο γονέας περνάει ΟΛΗ την υπόλοιπη ουρά και προχωράμε έναν-έναν εδώ, χωρίς
  // να χρειάζεται να ξαναφορτωθεί η οθόνη της συμφωνίας ανάμεσα.
  const queue = useMemo(() => {
    const ids = (queueIds ?? revieweeId ?? '').split('|').filter(Boolean);
    const names = (queueNames ?? revieweeName ?? '').split('|');
    return ids.map((id, i) => ({ id, name: names[i] ?? '' }));
  }, [queueIds, queueNames, revieweeId, revieweeName]);
  const [queueIndex, setQueueIndex] = useState(0);
  const current = queue[queueIndex] ?? { id: revieweeId, name: revieweeName ?? '' };
  const isLastInQueue = queueIndex >= queue.length - 1;

  const [rating, setRating] = useState(0);
  const [tags, setTags] = useState<ReviewTag[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmingNoPhoto, setConfirmingNoPhoto] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const canAttachPhoto = allowPhoto === '1';

  async function onAddPhoto(fromCamera: boolean) {
    if (!user) return;
    setUploadingPhoto(true);
    setError(null);
    try {
      const url = await pickReviewPhoto(user.id, fromCamera);
      if (url) setPhotoUrl(url);
    } catch (e) {
      setError(getErrorMessage(e, t('review.errorSubmitFailed')));
    } finally {
      setUploadingPhoto(false);
    }
  }

  const visibleTags = useMemo<ReviewTag[]>(() => {
    if (rating === 0) return [];
    if (rating <= 2) return NEGATIVE_TAGS;
    if (rating === 3) return [...POSITIVE_TAGS, ...NEGATIVE_TAGS];
    return POSITIVE_TAGS;
  }, [rating]);

  function toggleTag(tag: ReviewTag) {
    setError(null);
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  }

  function onRatingChange(val: number) {
    setRating(val);
    setTags([]);
  }

  function onSubmit() {
    setError(null);
    if (rating < 1) {
      setError(t('review.error_rating'));
      return;
    }
    if (!user || !agreementId || !current.id) return;
    if (canAttachPhoto && !photoUrl) {
      setConfirmingNoPhoto(true);
      return;
    }
    setConfirming(true);
  }

  async function doSubmit() {
    if (!user || !agreementId || !current.id) return;
    setLoading(true);
    setError(null);
    try {
      await createReview({ agreementId, reviewerId: user.id, revieweeId: current.id, rating, tags, photoUrl });
      setConfirming(false);

      const proceed = () => {
        if (isLastInQueue) {
          void maybeOfferAddToCircle(current.id, current.name);
        } else {
          // Επόμενος στην ουρά — καθαρό ξεκίνημα από το βήμα 1, ίδια οθόνη.
          setQueueIndex((i) => i + 1);
          setRating(0);
          setTags([]);
          setPhotoUrl(null);
          setWizardStep(1);
        }
      };

      // Δεν αναφέρουμε ποτέ ότι η βαθμολογία "δεν άλλαξε" σε σχέση με
      // προηγούμενη αξιολόγηση (anti-grinding trust score) — ο χρήστης θα
      // ένιωθε ότι η γνώμη του δεν μετράει, κάτι που ρίχνει το engagement.
      appAlert('', t('review.recordedTitle'), [{ text: t('common.ok'), onPress: proceed }]);
    } catch (e) {
      setConfirming(false);
      setError(getErrorMessage(e, t('review.errorSubmitFailed')));
    } finally {
      setLoading(false);
    }
  }

  async function maybeOfferAddToCircle(otherId: string, otherName: string) {
    try {
      const status = await getConnectionStatus(otherId);
      if (status !== 'none') { router.back(); return; }
      appAlert(
        t('review.addToCircleTitle'),
        t('review.addToCircleBody', { name: otherName?.trim() || t('review.defaultCounterparty') }),
        [
          { text: t('common.no'), style: 'cancel', onPress: () => router.back() },
          {
            text: t('review.addToCircleAction'),
            onPress: async () => {
              try { await sendConnectionRequest(otherId); } catch { /* αγνόησε */ }
              router.back();
            },
          },
        ],
      );
    } catch {
      router.back();
    }
  }

  const starLabels = ['', t('review.starLabel_1'), t('review.starLabel_2'), t('review.starLabel_3'), t('review.starLabel_4'), t('review.starLabel_5')];
  const starLabel = starLabels[rating] ?? '';

  return (
    <ScrollView
      style={styles.flex}
      contentContainerStyle={[
        styles.content,
        { paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.lg },
      ]}
      keyboardShouldPersistTaps="handled"
    >
      <WizardLayout
        step={wizardStep}
        totalSteps={3}
        title={
          (wizardStep === 1 ? t('review.wizard_step1_title') : wizardStep === 2 ? t('review.wizard_step2_title') : t('review.wizard_step3_title'))
          + (queue.length > 1 ? ` (${queueIndex + 1}/${queue.length})` : '')
        }
        subtitle={
          wizardStep === 1
            ? t('review.wizard_step1_subtitle_named', { name: current.name?.trim() || t('review.defaultCounterparty') })
            : wizardStep === 2
            ? t('review.wizard_step2_subtitle')
            : t('review.permanentWarning')
        }
        onBack={wizardStep > 1 ? () => setWizardStep((s) => s - 1) : undefined}
        onClose={() => router.back()}
      />

      {queue.length > 1 && (
        <View style={styles.revieweeBanner}>
          <Ionicons name="person-circle" size={22} color={colors.primary} />
          <Text style={styles.revieweeBannerText} numberOfLines={1}>
            {t('review.nowReviewing', { name: current.name?.trim() || t('review.defaultCounterparty') })}
          </Text>
        </View>
      )}

      {/* ── Βήμα 1: Αστέρια ── */}
      {wizardStep === 1 && (
        <View style={styles.section}>
          <StarRating value={rating} onChange={onRatingChange} size={48} />
          {rating > 0 && (
            <Text style={[styles.label, { textAlign: 'center', color: rating >= 4 ? colors.success : rating === 3 ? colors.textMuted : colors.danger }]}>
              {starLabel}
            </Text>
          )}
          <ErrorText>{error}</ErrorText>
          <Button
            title={t('common.next')}
            onPress={() => {
              if (rating < 1) { setError(t('review.error_rating')); return; }
              setError(null);
              setWizardStep(2);
            }}
          />
        </View>
      )}

      {/* ── Βήμα 2: Tags ── */}
      {wizardStep === 2 && (
        <View style={styles.section}>
          {visibleTags.length > 0 ? (
            <View style={styles.tags}>
              {visibleTags.map((key) => {
                const selected = tags.includes(key);
                const negative = tagSentiment(key) === 'negative';
                return (
                  <Pressable
                    key={key}
                    onPress={() => toggleTag(key)}
                    style={[styles.tag, selected && (negative ? styles.tagNegSelected : styles.tagPosSelected)]}
                    accessibilityRole="button"
                  >
                    <Text style={[styles.tagText, selected && styles.tagTextSelected]}>{tagLabel(key)}</Text>
                  </Pressable>
                );
              })}
            </View>
          ) : (
            <Text style={styles.subtitle}>{t('review.noTagsForRating')}</Text>
          )}
          <ErrorText>{error}</ErrorText>
          <Button
            title={t('common.next')}
            onPress={() => {
              if (tags.length === 0) { setError(t('review.error_tagsRequired')); return; }
              setError(null);
              setWizardStep(3);
            }}
          />
        </View>
      )}

      {/* ── Βήμα 3: Επιβεβαίωση ── */}
      {wizardStep === 3 && (
        <View style={styles.section}>
          <View style={styles.reviewSummary}>
            <Text style={styles.reviewSummaryTitle}>{t('review.summary_title')}</Text>
            <View style={styles.reviewSummaryRow}>
              <Text style={styles.reviewSummaryLabel}>{t('review.summary_rating')}</Text>
              <Text style={styles.reviewSummaryValue}>{'★'.repeat(rating)}{'☆'.repeat(5 - rating)} {starLabel}</Text>
            </View>
            {tags.length > 0 && (
              <View style={styles.reviewSummaryRow}>
                <Text style={styles.reviewSummaryLabel}>{t('review.summary_tags')}</Text>
                <Text style={styles.reviewSummaryValue}>{tags.map(tagLabel).join(', ')}</Text>
              </View>
            )}
          </View>

          {canAttachPhoto && (
            <View style={styles.photoSection}>
              <Text style={styles.label}>{t('review.photoLabel')}</Text>
              {photoUrl ? (
                <View style={styles.photoPreviewWrap}>
                  <Image source={{ uri: photoUrl }} style={styles.photoPreview} />
                  <Pressable
                    style={styles.photoRemoveBtn}
                    onPress={() => setPhotoUrl(null)}
                    accessibilityRole="button"
                  >
                    <Ionicons name="close" size={16} color="#fff" />
                  </Pressable>
                </View>
              ) : (
                <View style={styles.photoBtnRow}>
                  <Pressable
                    style={[styles.photoBox, uploadingPhoto && styles.photoBoxDisabled]}
                    onPress={() => onAddPhoto(false)}
                    disabled={uploadingPhoto}
                    accessibilityRole="button"
                  >
                    {uploadingPhoto ? (
                      <ActivityIndicator color={colors.primary} />
                    ) : (
                      <>
                        <Ionicons name="image-outline" size={26} color={colors.primary} />
                        <Text style={styles.photoBoxText}>{t('review.photoAddFromGallery')}</Text>
                      </>
                    )}
                  </Pressable>
                  <Pressable
                    style={[styles.photoBox, uploadingPhoto && styles.photoBoxDisabled]}
                    onPress={() => onAddPhoto(true)}
                    disabled={uploadingPhoto}
                    accessibilityRole="button"
                  >
                    <Ionicons name="camera-outline" size={26} color={colors.primary} />
                    <Text style={styles.photoBoxText}>{t('review.photoTakePhoto')}</Text>
                  </Pressable>
                </View>
              )}
            </View>
          )}

          <Text style={styles.subtitle}>
            {t('review.publicPermanentNotice', { name: current.name?.trim() || t('review.defaultCounterpartyGen') })}
          </Text>
          <ErrorText>{error}</ErrorText>
          <Button title={isLastInQueue ? t('review.action_submit') : t('review.action_submitAndNext')} onPress={onSubmit} loading={loading} />
        </View>
      )}

      <ConfirmModal
        visible={confirmingNoPhoto}
        title={t('review.confirmNoPhoto_title')}
        message={t('review.confirmNoPhoto_body')}
        confirmLabel={t('review.confirmNoPhoto_confirm')}
        cancelLabel={t('review.confirmNoPhoto_cancel')}
        onConfirm={() => { setConfirmingNoPhoto(false); setConfirming(true); }}
        onCancel={() => setConfirmingNoPhoto(false)}
      />

      <ConfirmModal
        visible={confirming}
        title={t('review.confirm_title')}
        message={t('review.publicPermanentNotice', { name: current.name?.trim() || t('review.defaultCounterpartyGen') })}
        confirmLabel={t('review.confirm_label')}
        loading={loading}
        onConfirm={doSubmit}
        onCancel={() => setConfirming(false)}
      />
    </ScrollView>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    flex: { flex: 1, backgroundColor: colors.background },
    content: {
      flexGrow: 1,
      paddingHorizontal: spacing.lg,
      gap: spacing.lg,
    },
    topBar: { flexDirection: 'row' },
    back: {
      color: colors.primaryLight,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    header: { gap: spacing.sm },
    title: {
      color: colors.text,
      fontSize: fontSize.xl,
      fontWeight: '700',
    },
    subtitle: {
      color: colors.textMuted,
      fontSize: fontSize.md,
    },
    revieweeBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: 'rgba(201,168,67,0.12)',
      borderWidth: 1,
      borderColor: 'rgba(201,168,67,0.35)',
      borderRadius: radius.md,
      paddingVertical: spacing.sm,
      paddingHorizontal: spacing.md,
    },
    revieweeBannerText: { color: colors.text, fontSize: fontSize.md, fontWeight: '800', flex: 1 },
    section: { gap: spacing.sm },
    label: {
      color: colors.textMuted,
      fontSize: fontSize.sm,
      fontWeight: '600',
    },
    tags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: spacing.sm,
    },
    tag: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radius.full,
      paddingVertical: 8,
      paddingHorizontal: spacing.md,
    },
    tagPosSelected: {
      backgroundColor: colors.success,
      borderColor: colors.success,
    },
    tagNegSelected: {
      backgroundColor: colors.danger,
      borderColor: colors.danger,
    },
    tagText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      fontWeight: '600',
    },
    tagTextSelected: {
      color: colors.textOnPrimary,
    },
    reviewSummary: { backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.border, padding: spacing.lg, gap: spacing.md },
    reviewSummaryTitle: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    reviewSummaryRow: { flexDirection: 'row', justifyContent: 'space-between', gap: spacing.sm },
    reviewSummaryLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '500' },
    reviewSummaryValue: { color: colors.text, fontSize: fontSize.sm, fontWeight: '600', flex: 1, textAlign: 'right' },
    error: {
      color: colors.danger,
      fontSize: fontSize.sm,
    },
    photoSection: { gap: spacing.xs },
    photoBtnRow: { flexDirection: 'row', gap: spacing.sm },
    photoBox: {
      flex: 1,
      height: 96,
      borderRadius: radius.lg,
      borderWidth: 2,
      borderStyle: 'dashed',
      borderColor: colors.primary,
      backgroundColor: colors.surfaceAlt,
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
    },
    photoBoxDisabled: { opacity: 0.6 },
    photoBoxText: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '700', textAlign: 'center' },
    photoPreviewWrap: { alignSelf: 'flex-start' },
    photoPreview: { width: 140, height: 140, borderRadius: radius.lg, backgroundColor: colors.surfaceAlt },
    photoRemoveBtn: {
      position: 'absolute',
      top: -8,
      right: -8,
      width: 26,
      height: 26,
      borderRadius: 13,
      backgroundColor: colors.danger,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
}
